import { Button } from '@protoss/ui';
import * as React from 'react';
import { useDataCatalogSettings } from '../hooks/useDataCatalogSettings';
import { dataCatalogApi } from '../services/dataCatalogApi';

export interface DataCatalogSettingsPaneHandle {
  enterEdit: () => void;
  cancelEdit: () => void;
}

interface DataCatalogSettingsPaneProps {
  projectUuid: string;
  onSaved?: () => void;
  onEditModeChange?: (editing: boolean) => void;
}

export const DataCatalogSettingsPane = React.forwardRef<
  DataCatalogSettingsPaneHandle,
  DataCatalogSettingsPaneProps
>(function DataCatalogSettingsPane({ projectUuid, onSaved, onEditModeChange }, ref) {
  const { settings, loading, reload } = useDataCatalogSettings(projectUuid);

  const [editMode, setEditMode] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // Edit form state
  const [catalogName, setCatalogName] = React.useState('');
  const [schemaName, setSchemaName] = React.useState('');
  const [allowedTables, setAllowedTables] = React.useState('');

  React.useImperativeHandle(ref, () => ({
    enterEdit,
    cancelEdit,
  }));

  function enterEdit() {
    setCatalogName(settings?.catalog_name ?? '');
    setSchemaName(settings?.schema_name ?? '');
    setAllowedTables(settings?.allowed_tables ?? '');
    setSaveError(null);
    setEditMode(true);
    onEditModeChange?.(true);
  }

  function cancelEdit() {
    setEditMode(false);
    setSaveError(null);
    onEditModeChange?.(false);
  }

  async function handleSave() {
    const trimmedCatalog = catalogName.trim();
    if (!trimmedCatalog) {
      setSaveError('Catalog name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await dataCatalogApi.saveSettings(projectUuid, {
        catalog_name: trimmedCatalog,
        schema_name: schemaName.trim() || null,
        allowed_tables: allowedTables.trim() || null,
      });
      await reload();
      setEditMode(false);
      onEditModeChange?.(false);
      onSaved?.();
    } catch (err) {
      setSaveError((err as Error).message ?? 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="dc-settings">
        <p className="dc-settings__unconfigured">Loading…</p>
      </div>
    );
  }

  if (editMode) {
    return (
      <div className="dc-settings">
        <div className="dc-settings__field">
          <label className="dc-settings__label" htmlFor="dc-catalog-name">
            Catalog Name <span aria-hidden="true">*</span>
          </label>
          <input
            id="dc-catalog-name"
            type="text"
            className={`dc-settings__input${saveError && !catalogName.trim() ? ' dc-settings__input--error' : ''}`}
            value={catalogName}
            onChange={(e) => setCatalogName(e.target.value)}
            placeholder="e.g. main"
            autoFocus
            spellCheck={false}
          />
          <span className="dc-settings__hint">
            The Unity Catalog name to scope this project to.
          </span>
        </div>

        <div className="dc-settings__field">
          <label className="dc-settings__label" htmlFor="dc-schema-name">
            Schema Name
          </label>
          <input
            id="dc-schema-name"
            type="text"
            className="dc-settings__input"
            value={schemaName}
            onChange={(e) => setSchemaName(e.target.value)}
            placeholder="Leave blank to allow all schemas"
            spellCheck={false}
          />
          <span className="dc-settings__hint">Optional. Restricts browsing to this schema.</span>
        </div>

        <div className="dc-settings__field">
          <label className="dc-settings__label" htmlFor="dc-allowed-tables">
            Allowed Tables
          </label>
          <input
            id="dc-allowed-tables"
            type="text"
            className="dc-settings__input"
            value={allowedTables}
            onChange={(e) => setAllowedTables(e.target.value)}
            placeholder="Comma-separated, or blank for all"
            spellCheck={false}
          />
          <span className="dc-settings__hint">
            Optional. Comma-separated list of table names to expose.
          </span>
        </div>

        {saveError && <p className="dc-settings__error">{saveError}</p>}

        <div className="dc-settings__actions">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => void handleSave()}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={cancelEdit} disabled={saving}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Read view
  if (!settings) {
    return (
      <div className="dc-settings">
        <p className="dc-settings__unconfigured">
          No catalog scope configured. Click Edit to set one up.
        </p>
      </div>
    );
  }

  return (
    <div className="dc-settings">
      <div className="dc-settings__field">
        <span className="dc-settings__label">Catalog Name</span>
        <span className="dc-settings__value">{settings.catalog_name}</span>
      </div>

      <div className="dc-settings__field">
        <span className="dc-settings__label">Schema Name</span>
        {settings.schema_name ? (
          <span className="dc-settings__value">{settings.schema_name}</span>
        ) : (
          <span className="dc-settings__value dc-settings__value--empty">- all schemas</span>
        )}
      </div>

      <div className="dc-settings__field">
        <span className="dc-settings__label">Allowed Tables</span>
        {settings.allowed_tables ? (
          <span className="dc-settings__value">{settings.allowed_tables}</span>
        ) : (
          <span className="dc-settings__value dc-settings__value--empty">- all tables</span>
        )}
      </div>
    </div>
  );
});
